import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { Observable, Subscription, switchMap, tap } from 'rxjs';

import {
  AccountWithLocation,
  LocationSwitcherService,
} from './location-switcher.service';
import { SelectedLocation } from './models';
import { AppIcon, AppIconColor, AppIconSize } from '@tablesready/frontend-icons';
import { NavigationService } from '@tablesready/frontend-navigation';
import { Router } from '@angular/router';
import {
  BillingVisibility,
  FrontendPaidFeatureService,
} from '@tablesready/frontend-paid-feature-alert';
import { LocationSwitcherBadgeService } from './location-badge.service';

@Component({
  selector: 'tablesready-location-switcher',
  templateUrl: './location-switcher.component.html',
  styleUrls: ['./location-switcher.component.scss'],
  standalone: false,
})
export class LocationSwitcherComponent implements OnInit, OnDestroy {
  @Input() id = 'switcher';
  @Input() width = '160px';
  @Input() popoverSide: 'left' | 'right' = 'left';

  accounts: AccountWithLocation[] = [];
  selectedLocation?: SelectedLocation;
  selectedAccount?: string;
  icon = AppIcon;
  size = AppIconSize;
  iconColor = AppIconColor;

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly locationSwitcherService: LocationSwitcherService,
    private readonly locationSwitcherBadgeService: LocationSwitcherBadgeService,
    private readonly navService: NavigationService,
    private readonly paidService: FrontendPaidFeatureService,
    private readonly router: Router,
  ) { }

  ngOnInit(): void {
    this.subscriptions.add(this.locationSwitcherService.initData().subscribe());

    const locationSub = this.locationSwitcherService.selectedLocation$
      .pipe(
        switchMap((loc) => {
          this.selectedLocation = loc;
          if (loc) {
            this.selectedAccount = loc.account.name;
          }

          return this.navService.navEvents$;
        }),
        tap((nav) => {
          const starterPlanProhibitedRoutes = [
            '/floorplan',
            '/togo',
            '/settings/preferences/party-fields',
            '/settings/preferences/automations',
            '/settings/preferences/integrations',
            '/settings/preferences/services',
            '/settings/preferences/resources/list',
            '/settings/preferences/resources',
          ];
          const freePlanProhibitedRoutes = [
            ...starterPlanProhibitedRoutes,
            '/analytics',
          ];

          if (
            this.paidService.billingsVisibility === BillingVisibility.HIDDEN_ON_MOBILE &&
            freePlanProhibitedRoutes.includes(this.navService.currentUrl)
          ) {
            void this.router.navigateByUrl('/waitlist');
          }

          if (
            this.paidService.billingsVisibility === BillingVisibility.STARTER_PLAN_OUTSIDE_US &&
            starterPlanProhibitedRoutes.includes(this.navService.currentUrl)
          ) {
            void this.router.navigateByUrl('/waitlist');
          }
        }),
      )
      .subscribe();
    this.subscriptions.add(locationSub);

    const accountSub = this.locationSwitcherService.accounts$
      .subscribe((accounts) => {
        this.accounts = accounts;
      });
    this.subscriptions.add(accountSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onPopoverOpen(): void {
    const locationIds = this.accounts
      .flatMap(account => account.locations.map(loc => loc._id));
    if (locationIds.length) {
      this.locationSwitcherBadgeService.refreshCounts(locationIds).subscribe();
    }
  }

  selectLocation(locationId: string): void {
    this.locationSwitcherService.selectLocation(locationId);
  }

  getBadgeCount$(locationId: string): Observable<number> {
    return this.locationSwitcherBadgeService.getBadgeCount$(locationId);
  }
}
